import { readShowroomState, refreshFromPage, showroomRequest, showroomToast } from "./showroom-api";

// Selecting a member's column shows their actions; for the owner it also opens
// the Showroom styles panel, whose Add to lineup buttons target that member.

const desktopQuery = () => window.matchMedia(`(min-width: ${window.themeBreakpoints?.["tablet-wide"] || 1024}px)`);

class ShowroomLineupSlider extends HTMLElement {
  connectedCallback() {
    this.page = this.closest("showroom-page");
    this.viewport = this.querySelector(".showroom-lineup__viewport");
    this.track = this.querySelector("[data-lineup-track]");
    this.prevButton = this.querySelector("[data-lineup-prev]");
    this.nextButton = this.querySelector("[data-lineup-next]");
    this.panel = this.querySelector("[data-lineup-panel]");
    // Cards are keyed by member id: one email can hold more than one seat.
    this.activeMemberId = "";
    this.activeEmail = "";

    if (!this.track) return;

    this.prevButton?.addEventListener("click", () => this.scrollByPage(-1));
    this.nextButton?.addEventListener("click", () => this.scrollByPage(1));
    this.viewport.addEventListener(
      "scroll",
      () => {
        this.updateArrows();
        this.updateNotch({ follow: true });
      },
      { passive: true },
    );

    this.handleResize = () => {
      this.updateArrows();
      this.updateNotch({ follow: true });
    };

    // Also fires when the lineup tab is first shown (size goes from 0).
    this.resizeObserver = new ResizeObserver(() => this.updateNotch({ follow: true }));
    this.resizeObserver.observe(this);
    window.addEventListener("resize", this.handleResize);

    this.track.addEventListener("click", (event) => {
      const toggle = event.target.closest("[data-lineup-card-toggle]");
      if (!toggle) return;

      this.toggleMember(toggle.closest("[data-lineup-card]"));
    });

    if ("canReorder" in this.dataset) this.initReorder();

    this.updateArrows();
    this.restoreSelection();
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.handleResize);
    this.resizeObserver?.disconnect();
    if (this.syncDraggable) this.desktopQuery?.removeEventListener("change", this.syncDraggable);
  }

  get cards() {
    return [...this.track.querySelectorAll("[data-lineup-card]")];
  }

  // Scrolling ------------------------------------------------------------

  scrollByPage(direction) {
    this.viewport.scrollBy({ left: direction * this.viewport.clientWidth * 0.8, behavior: "smooth" });
  }

  updateArrows() {
    const maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
    const scrollable = maxScroll > 1;

    if (this.prevButton) this.prevButton.hidden = !scrollable || this.viewport.scrollLeft <= 1;
    if (this.nextButton) this.nextButton.hidden = !scrollable || this.viewport.scrollLeft >= maxScroll - 1;
  }

  // Selection ------------------------------------------------------------

  restoreSelection() {
    const remembered = this.page?.selectedLineupMemberId;
    const card =
      (remembered && this.cards.find((entry) => entry.dataset.memberId === remembered)) ||
      // The owner lands with the first member open, ready to add styles.
      ("isAdmin" in this.dataset && remembered === undefined ? this.cards[0] : null);

    if (card) this.selectMember(card, { scroll: false });
  }

  toggleMember(card) {
    if (!card) return;

    if (card.dataset.memberId === this.activeMemberId) {
      this.clearSelection();
    } else {
      this.selectMember(card);
    }
  }

  selectMember(card, { scroll = true } = {}) {
    this.activeMemberId = card.dataset.memberId;
    this.activeEmail = card.dataset.memberEmail;

    this.cards.forEach((entry) => {
      const active = entry === card;

      entry.classList.toggle("is-active", active);
      entry.querySelectorAll("[data-lineup-card-toggle]").forEach((toggle) => toggle.setAttribute("aria-pressed", String(active)));
    });

    if (this.panel) this.panel.hidden = false;
    window.requestAnimationFrame(() => this.updateNotch());

    if (scroll) card.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });

    this.notifySelection();
  }

  // `follow`: the row is scrolling, so the arrow tracks the column without
  // easing behind it.
  updateNotch({ follow = false } = {}) {
    if (!this.panel || this.panel.hidden) return;

    this.panel.classList.toggle("is-notch-following", follow);

    const card = this.activeMemberId && this.cards.find((entry) => entry.dataset.memberId === this.activeMemberId);
    const frame = card?.querySelector(".showroom-lineup-card__frame") || card;
    if (!frame) {
      this.panel.classList.remove("has-notch");
      return;
    }

    const column = frame.getBoundingClientRect();
    const panel = this.panel.getBoundingClientRect();
    const viewport = this.viewport.getBoundingClientRect();

    // Not laid out (the lineup tab is hidden): placed once it shows, by the
    // ResizeObserver in connectedCallback.
    if (!panel.width || !column.width) return;
    const center = column.left + column.width / 2;
    // On mobile the row scrolls past the screen edges while the panel stays
    // inset: the notch shows only over the panel (half its 48px width in).
    const visible =
      center >= Math.max(viewport.left, panel.left + 24) && center <= Math.min(viewport.right, panel.right - 24);

    this.panel.style.setProperty("--lineup-notch", `${center - panel.left}px`);
    this.panel.classList.toggle("has-notch", visible);

    // The first position is set without animating; moves after that slide.
    if (visible && !this.panel.classList.contains("has-notch-placed")) {
      window.requestAnimationFrame(() => this.panel?.classList.add("has-notch-placed"));
    }
  }

  clearSelection() {
    this.activeMemberId = "";
    this.activeEmail = "";
    this.cards.forEach((entry) => entry.classList.remove("is-active"));
    if (this.panel) this.panel.hidden = true;

    this.notifySelection();
  }

  notifySelection() {
    this.dispatchEvent(
      new CustomEvent("showroom:lineup-member-selected", {
        bubbles: true,
        detail: { email: this.activeEmail, memberId: this.activeMemberId },
      }),
    );
  }

  // Reordering -----------------------------------------------------------

  // The app stores the lineup as one flat item list and moves a single item, so
  // moving a member's first item to the target's first slot swaps the columns.
  initReorder() {
    this.track.addEventListener("dragstart", (event) => {
      const card = event.target.closest("[data-lineup-card][data-lineup-index]");
      if (!card || !desktopQuery().matches) {
        event.preventDefault();
        return;
      }

      this.draggedCard = card;
      card.classList.add("is-dragging");
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", card.dataset.memberEmail);
    });

    this.track.addEventListener("dragover", (event) => {
      const target = event.target.closest("[data-lineup-card][data-lineup-index]");
      if (!this.draggedCard || !target || target === this.draggedCard) return;

      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      this.cards.forEach((entry) => entry.classList.toggle("is-drop-target", entry === target));
    });

    this.track.addEventListener("drop", (event) => {
      const target = event.target.closest("[data-lineup-card][data-lineup-index]");
      if (!this.draggedCard || !target || target === this.draggedCard) return;

      event.preventDefault();
      this.moveCard(this.draggedCard, target);
    });

    this.track.addEventListener("dragend", () => {
      this.draggedCard?.classList.remove("is-dragging");
      this.cards.forEach((entry) => entry.classList.remove("is-drop-target"));
      this.draggedCard = null;
    });

    this.desktopQuery = desktopQuery();
    this.syncDraggable = () => {
      const enabled = this.desktopQuery.matches;
      this.cards.forEach((card) => {
        card.draggable = enabled && card.hasAttribute("data-lineup-index");
      });
    };

    this.syncDraggable();
    this.desktopQuery.addEventListener("change", this.syncDraggable);
  }

  async moveCard(card, target) {
    const from = Number(card.dataset.lineupIndex);
    const to = Number(target.dataset.lineupIndex);

    // Optimistically show the new order while the showroom app saves it.
    if (from < to) {
      target.after(card);
    } else {
      target.before(card);
    }

    const state = readShowroomState(this.page || document);
    const expected = [...(state.lineup || [])];
    const [moved] = expected.splice(from, 1);
    expected.splice(to, 0, moved);
    const expectedOrder = expected.map((item) => `${item.member}:${item.variant}`).join("|");

    this.classList.add("is-loading");

    const { ok } = await showroomRequest("lineupOrderChange", {
      body: { from: String(from), to: String(to) },
      showroom: this.page?.dataset.showroomId,
    });

    if (!ok) {
      showroomToast(this.page?.dataset.localeGenericError);
      window.location.reload();
      return;
    }

    await refreshFromPage(["[data-showroom-state]", "#showroom-panel-lineup", "#showroom-modal-export [data-export-canvas]"], {
      isReady: (doc) =>
        (readShowroomState(doc).lineup || []).map((item) => `${item.member}:${item.variant}`).join("|") === expectedOrder,
    });
  }
}

// <showroom-lineup-slides>: the styles inside one member's column, shown one
// at a time with prev/next arrows and dots.
class ShowroomLineupSlides extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-slides-track]");
    this.slides = [...this.querySelectorAll("[data-slide]")];
    this.prevButton = this.querySelector("[data-slides-prev]");
    this.nextButton = this.querySelector("[data-slides-next]");
    this.dots = [...this.querySelectorAll("[data-slides-dots] > *")];

    if (!this.track || this.slides.length < 2) return;

    this.prevButton?.addEventListener("click", (event) => {
      event.stopPropagation();
      this.goTo(this.index - 1);
    });
    this.nextButton?.addEventListener("click", (event) => {
      event.stopPropagation();
      this.goTo(this.index + 1);
    });
    this.track.addEventListener("scroll", () => this.update(), { passive: true });

    this.update();
  }

  get index() {
    const width = this.track.clientWidth || 1;
    return Math.round(this.track.scrollLeft / width);
  }

  goTo(index) {
    const clamped = Math.max(0, Math.min(index, this.slides.length - 1));
    this.track.scrollTo({ left: this.slides[clamped].offsetLeft, behavior: "smooth" });
  }

  // After a slide is removed (see showroom-page.js removeLineupSlide): the
  // counters, dots and arrows follow the slides that are left.
  refresh() {
    this.slides = [...this.querySelectorAll("[data-slide]")];
    const total = this.slides.length;

    this.slides.forEach((slide, slideIndex) => {
      const counter = slide.querySelector(".showroom-lineup-card__counter");
      if (!counter) return;
      if (total < 2) counter.remove();
      else counter.textContent = `${slideIndex + 1} / ${total}`;
    });

    this.dots.slice(total).forEach((dot) => dot.remove());
    this.dots = this.dots.slice(0, total);

    if (total < 2) {
      this.prevButton?.remove();
      this.nextButton?.remove();
      this.querySelector("[data-slides-dots]")?.remove();
      return;
    }

    if (this.track) this.track.scrollLeft = Math.min(this.track.scrollLeft, this.slides[total - 1].offsetLeft);
    this.update();
  }

  update() {
    const index = this.index;

    if (this.prevButton) this.prevButton.hidden = index <= 0;
    if (this.nextButton) this.nextButton.hidden = index >= this.slides.length - 1;
    this.dots.forEach((dot, dotIndex) => dot.classList.toggle("is-active", dotIndex === index));
  }
}

if (!customElements.get("showroom-lineup-slider")) {
  customElements.define("showroom-lineup-slider", ShowroomLineupSlider);
}

if (!customElements.get("showroom-lineup-slides")) {
  customElements.define("showroom-lineup-slides", ShowroomLineupSlides);
}
